import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Database, Activity, Lock, RefreshCw, CheckCircle2, XCircle, Clock, Users, FileWarning, Webhook, Smartphone } from 'lucide-react';

const SEV_COLORS = { CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-red-500 text-white', MEDIUM: 'bg-amber-500 text-white', LOW: 'bg-slate-400 text-white', INFO: 'bg-blue-400 text-white', OK: 'bg-green-500 text-white', WARNING: 'bg-amber-500 text-white', ERROR: 'bg-red-500 text-white' };

export default function SecurityDashboardPage() {
  const [health, setHealth] = useState(null);
  const [permAudit, setPermAudit] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, paRes, diRes] = await Promise.all([
        api.get('/security/system/health').catch(() => null),
        api.get('/security/permission-audit?pageSize=1').catch(() => null),
        api.get('/security/data-integrity?pageSize=1').catch(() => null),
      ]);
      if (hRes) setHealth(hRes.data.data);
      if (paRes?.data?.items?.[0]) {
        const rid = paRes.data.items[0].id;
        const full = await api.get(`/security/permission-audit/${rid}`).catch(() => null);
        if (full) setPermAudit(full.data.data);
      }
      if (diRes?.data?.items?.[0]) {
        const rid = diRes.data.items[0].id;
        const full = await api.get(`/security/data-integrity/${rid}`).catch(() => null);
        if (full) setIntegrity(full.data.data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAudit = async (type) => {
    setRunning(type);
    try {
      if (type === 'permission') {
        const r = await api.post('/security/permission-audit/run');
        setPermAudit(r.data.data);
        toast.success(`Permission audit: ${r.data.data.totalIssues} issues found`);
      } else if (type === 'integrity') {
        const r = await api.post('/security/data-integrity/run');
        setIntegrity(r.data.data);
        toast.success(`Integrity check: ${r.data.data.totalIssues} issues found`);
      } else if (type === 'health') {
        const r = await api.get('/security/system/health');
        setHealth(r.data.data);
        toast.success('Health check complete');
      }
    } catch { toast.error('Failed to run check'); }
    setRunning('');
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  // Aggregate alert cards
  const excessiveAccess = permAudit?.issuesFound?.filter(i => i.checkType === 'EXCESSIVE_PERMISSIONS').length || 0;
  const noMfa = permAudit?.issuesFound?.filter(i => i.checkType === 'ADMIN_NO_MFA').length || 0;
  const noFunding = integrity?.issuesFound?.filter(i => i.checkType === 'PARTICIPANT_NO_FUNDING').length || 0;
  const portalNoConsent = integrity?.issuesFound?.filter(i => i.checkType === 'PORTAL_NO_CONSENT').length || 0;
  const commsFailed = health?.components?.find(c => c.component === 'COMMUNICATIONS')?.metrics?.smsFailed + (health?.components?.find(c => c.component === 'COMMUNICATIONS')?.metrics?.emailFailed || 0) || 0;
  const whFailed = health?.components?.find(c => c.component === 'WEBHOOKS')?.metrics?.failedDeliveries || 0;
  const syncFailed = health?.components?.find(c => c.component === 'SYNC_ENGINE')?.metrics?.failedSyncs || 0;

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="w-6 h-6" />Security Dashboard</h1>
        <Button variant="outline" onClick={() => { runAudit('health'); }} disabled={!!running} data-testid="refresh-all"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="alert-cards">
        <AlertCard icon={Users} label="Excessive Access" value={excessiveAccess} color={excessiveAccess > 0 ? 'text-red-600' : 'text-green-600'} />
        <AlertCard icon={Lock} label="No MFA" value={noMfa} color={noMfa > 0 ? 'text-amber-600' : 'text-green-600'} />
        <AlertCard icon={FileWarning} label="No Funding" value={noFunding} color={noFunding > 0 ? 'text-red-600' : 'text-green-600'} />
        <AlertCard icon={Users} label="No Consent" value={portalNoConsent} color={portalNoConsent > 0 ? 'text-red-600' : 'text-green-600'} />
        <AlertCard icon={XCircle} label="Comms Failed" value={commsFailed} color={commsFailed > 0 ? 'text-amber-600' : 'text-green-600'} />
        <AlertCard icon={Webhook} label="Webhook Fails" value={whFailed} color={whFailed > 0 ? 'text-amber-600' : 'text-green-600'} />
        <AlertCard icon={Smartphone} label="Sync Fails" value={syncFailed} color={syncFailed > 0 ? 'text-amber-600' : 'text-green-600'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* System Health */}
        <Card data-testid="health-panel">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" />System Health</CardTitle>
            <div className="flex items-center gap-2">
              {health && <Badge className={SEV_COLORS[health.overallStatus]}>{health.overallStatus}</Badge>}
              <Button size="sm" variant="ghost" onClick={() => runAudit('health')} disabled={running === 'health'}><RefreshCw className={`w-3 h-3 ${running === 'health' ? 'animate-spin' : ''}`} /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {health?.components?.map(c => (
              <div key={c.component} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {c.status === 'OK' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : c.status === 'WARNING' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                  <span className="font-medium">{c.component}</span>
                </div>
                <div className="text-right">
                  <Badge className={SEV_COLORS[c.status]}>{c.status}</Badge>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.message?.substring(0, 50)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Permission Audit */}
        <Card data-testid="permission-audit-panel">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" />Permission Audit</CardTitle>
            <div className="flex items-center gap-2">
              {permAudit && <Badge className={SEV_COLORS[permAudit.severity]}>{permAudit.totalIssues} issues</Badge>}
              <Button size="sm" variant="ghost" onClick={() => runAudit('permission')} disabled={running === 'permission'} data-testid="run-perm-audit"><RefreshCw className={`w-3 h-3 ${running === 'permission' ? 'animate-spin' : ''}`} /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {permAudit?.issuesFound?.length > 0 ? (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {permAudit.issuesFound.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <Badge className={`${SEV_COLORS[issue.severity]} text-[10px] shrink-0 mt-0.5`}>{issue.severity}</Badge>
                    <span className="text-muted-foreground">{issue.message}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm text-center py-4">No permission issues — run audit to check</p>}
          </CardContent>
        </Card>

        {/* Data Integrity */}
        <Card data-testid="integrity-panel">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4" />Data Integrity</CardTitle>
            <div className="flex items-center gap-2">
              {integrity && <Badge className={SEV_COLORS[integrity.severity]}>{integrity.totalIssues} issues</Badge>}
              <Button size="sm" variant="ghost" onClick={() => runAudit('integrity')} disabled={running === 'integrity'} data-testid="run-integrity"><RefreshCw className={`w-3 h-3 ${running === 'integrity' ? 'animate-spin' : ''}`} /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {integrity?.issuesFound?.length > 0 ? (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {integrity.issuesFound.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <Badge className={`${SEV_COLORS[issue.severity]} text-[10px] shrink-0 mt-0.5`}>{issue.severity}</Badge>
                    <span className="text-muted-foreground">{issue.message}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm text-center py-4">No integrity issues — run check</p>}
          </CardContent>
        </Card>

        {/* Security Alerts Summary */}
        <Card data-testid="security-alerts-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Security Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'Users with excessive permissions', count: excessiveAccess, type: 'EXCESSIVE_PERMISSIONS' },
              { label: 'Admin users without MFA', count: noMfa, type: 'ADMIN_NO_MFA' },
              { label: 'Participants without funding', count: noFunding, type: 'PARTICIPANT_NO_FUNDING' },
              { label: 'Portal users without consent', count: portalNoConsent, type: 'PORTAL_NO_CONSENT' },
              { label: 'Failed communications', count: commsFailed, type: 'COMMS_FAILED' },
              { label: 'Failed webhook deliveries', count: whFailed, type: 'WEBHOOK_FAILED' },
              { label: 'Sync failures', count: syncFailed, type: 'SYNC_FAILED' },
            ].filter(a => a.count > 0).map(alert => (
              <div key={alert.type} className="flex items-center justify-between text-sm">
                <span>{alert.label}</span>
                <Badge variant="destructive">{alert.count}</Badge>
              </div>
            ))}
            {excessiveAccess + noMfa + noFunding + portalNoConsent + commsFailed + whFailed + syncFailed === 0 && (
              <p className="text-green-600 text-sm text-center py-2 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" />All clear — no security alerts</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AlertCard({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="py-3 text-center">
        <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
